import { useEffect, useRef, useState } from 'react';
import { Alert, Box, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { LabelAnswer } from './labelAnswer';
import { asyncPostValidateReference } from '../../../../services/survey.service';

export type VerifyReferenceResult = {
    valid: boolean;
    message?: string;
    nombre?: string;
    email?: string;
};

type Props = {
    title?: string;
    placeholder?: string;
    required: false | boolean;
    onChange: (value: any) => void;
    idQuestion?: string;
    questionType?: string;
    value?: string;
    disabled?: boolean;
    dominantColor?: string;
    surveyId?: string;
    onVerifyReference?: (value: string) => Promise<VerifyReferenceResult>;
    onVerifiedChange?: (verified: boolean) => void;
    onValidationDataset?: (data: { nombre: string; email: string }) => void;
};

const FieldTextValidateAnswer = ({
    title,
    placeholder,
    required,
    onChange,
    idQuestion,
    questionType,
    value,
    disabled,
    dominantColor,
    surveyId,
    onVerifyReference,
    onVerifiedChange,
    onValidationDataset,
}: Props) => {
    const formik = useFormik({
        initialValues: {
            idQuestion,
            answer: '',
            type: questionType,
            why: '',
        },
        validationSchema: Yup.object({
            answer: (() => {
                let schema = Yup.string();
                if (required) {
                    schema = schema.required('La respuesta es obligatoria');
                }

                const allowedChars =
                    /^[A-Za-z0-9\u00C1\u00C9\u00CD\u00D3\u00DA\u00E1\u00E9\u00ED\u00F3\u00FA\u00F1\u00D1!@#$%^&*()_+\-=;:,.<>?/"' ]+$/;
                schema = schema.test(
                    'allowed-chars',
                    'Solo se permiten letras, números, espacios y los siguientes caracteres especiales: ! @ # $ % ^ & * ( ) _ + - = ; : , . < > ? / " \'',
                    (v) => v == null || v === '' || allowedChars.test(v)
                );
                schema = schema.max(2000, 'Máximo 2000 caracteres');

                return schema;
            })(),
        }),
        onSubmit: () => undefined,
    });

    const prevExternalValueRef = useRef(value);
    useEffect(() => {
        if (value === prevExternalValueRef.current) {
            return;
        }
        prevExternalValueRef.current = value;
        if (value === undefined || value === null) {
            return;
        }
        const next = String(value);
        if (next !== formik.values.answer) {
            void formik.setFieldValue('answer', next, false);
        }
    }, [value]);
    const answerText = formik.values.answer;
    const [referenceVerified, setReferenceVerified] = useState(false);
    const [validatingReference, setValidatingReference] = useState(false);
    const [referenceFeedback, setReferenceFeedback] = useState<string | null>(
        null
    );

    useEffect(() => {
        setReferenceVerified(false);
        setReferenceFeedback(null);
        onVerifiedChange?.(false);
    }, [answerText]);

    const trimmedAnswer = answerText.trim();
    const formikAnswerError = Boolean(formik.errors.answer);
    const emptyRequired = Boolean(required && !trimmedAnswer);
    const needsVerify = trimmedAnswer.length > 0;
    const verifyPending = needsVerify && !referenceVerified;

    useEffect(() => {
        onChange({
            idQuestion,
            answer: answerText,
            type: questionType,
            why: formik.values.why,
            error: formikAnswerError || emptyRequired || verifyPending,
        });
    }, [
        idQuestion,
        questionType,
        answerText,
        formik.values.why,
        formikAnswerError,
        emptyRequired,
        verifyPending,
    ]);

    const canCallApi = Boolean(surveyId && idQuestion);
    const verifyFn =
        onVerifyReference ??
        (canCallApi
            ? (text: string) =>
                asyncPostValidateReference({
                    survey: surveyId as string,
                    question: idQuestion as string,
                    value: text,
                })
            : undefined);

    const handleValidateClick = async () => {
        setReferenceFeedback(null);
        const errs = await formik.validateForm();
        if (Object.keys(errs).length > 0) {
            return;
        }
        if (!trimmedAnswer) {
            setReferenceFeedback('Escriba un valor antes de validar.');
            return;
        }
        if (!verifyFn) {
            setReferenceFeedback('No hay servicio de validación configurado.');
            return;
        }

        setValidatingReference(true);
        try {
            const result = await verifyFn(trimmedAnswer);
            if (result?.valid) {
                setReferenceVerified(true);
                setReferenceFeedback(null);
                onVerifiedChange?.(true);
                if (onValidationDataset) {
                    onValidationDataset({
                        nombre: String(result.nombre ?? ''),
                        email: String(result.email ?? ''),
                    });
                }
            } else {
                setReferenceVerified(false);
                onVerifiedChange?.(false);
                setReferenceFeedback(
                    result?.message ||
                        'El registro no está dentro de la validación.'
                );
            }
        } catch {
            setReferenceVerified(false);
            onVerifiedChange?.(false);
            setReferenceFeedback(
                'El registro no está dentro de la validación.'
            );
        } finally {
            setValidatingReference(false);
        }
    };

    return (
        <>
            <LabelAnswer
                text={title}
                isRequired={required}
                dominantColor={dominantColor}
            />
            <Box
                sx={{
                    px: { xs: 0, md: 10 },
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    flexWrap: 'wrap',
                    mt: 1,
                }}
            >
                <TextField
                    id="answer"
                    name="answer"
                    fullWidth
                    required={required}
                    variant="outlined"
                    value={answerText}
                    onChange={formik.handleChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    helperText={
                        Boolean(formik.errors.answer)
                            ? formik.errors.answer
                            : ''
                    }
                    error={Boolean(formik.errors.answer)}
                    InputProps={{
                        inputProps: {
                            style: {
                                fontFamily: 'Lucida Sans',
                                fontSize: '15px',
                            },
                        },
                    }}
                    sx={{
                        flex: '1 1 220px',
                        minWidth: 0,
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: '#ccc',
                            },
                            '&:hover fieldset': {
                                borderColor: dominantColor || '#36c',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: dominantColor || '#36c',
                            },
                        },
                        '& label.Mui-focused': {
                            color: dominantColor || '#36c',
                        },
                        '.MuiFormHelperText-root': {
                            textAlign: 'center',
                        },
                    }}
                />
                <LoadingButton
                    type="button"
                    variant="outlined"
                    size="medium"
                    loading={validatingReference}
                    disabled={disabled || !trimmedAnswer || !verifyFn}
                    onClick={handleValidateClick}
                    sx={{
                        flexShrink: 0,
                        mt: { xs: 0, sm: 0.5 },
                        borderRadius: '24px',
                        textTransform: 'none',
                        fontFamily: 'Lucida Sans',
                        fontSize: '14px',
                        borderColor: dominantColor || '#36c',
                        color: dominantColor || '#36c',
                    }}
                >
                    Validar
                </LoadingButton>
                {referenceVerified ? (
                    <CheckCircleIcon
                        sx={{
                            fontSize: '1.5rem',
                            flexShrink: 0,
                            mt: 1,
                            color: 'success.main',
                            opacity: 0.55,
                        }}
                        titleAccess="Validado"
                    />
                ) : null}
            </Box>
            {referenceFeedback ? (
                <Alert
                    severity="warning"
                    sx={{ mt: 1, maxWidth: 520, mx: 'auto' }}
                >
                    {referenceFeedback}
                </Alert>
            ) : null}
        </>
    );
};

export default FieldTextValidateAnswer;
